import {
  DEFAULT_ROUTINE_ICON,
  type Report,
  type Routine,
  type RoutineLibraryItem,
  useStores,
} from '@littlebird/sdk';
import {
  formatRoutineScheduleSummary,
  formatRoutineWeekDays,
  isRoutineRunUnread,
  parseRoutineRecurrenceRule,
  parseRoutineSchedule,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { motion, useAnimationControls } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { HiddenFromUserIcon } from '@/components/blocks/HiddenFromUserIcon';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, IconButton, Text, Time } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import { EntityIconChip, IconPicker } from '@/components/ui/icon-picker';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { SearchInput } from '@/components/ui/SearchInput';
import { Skeleton } from '@/components/ui/skeleton';
import { HeaderSearch } from '@/design-system-v2/header-search';
import { Page } from '@/design-system-v2/page';
import { useResumeRoutine } from '@/features/routines/hooks/use-resume-routine';
import { getRoutineActions } from '@/features/routines/routine-action-state';
import { RoutineTeamShareMenuItems } from '@/features/routines/routine-team-share';
import {
  refreshRoutineReads,
  routineReadState,
  useRoutineRefresh,
} from '@/features/routines/use-routine-refresh';
import { useVisibleRoutines } from '@/features/settings/proactive-agent.query';
import {
  RoutinesLimitWarning,
  useRoutinesLimitState,
} from '@/features/subscription';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { trackEvent } from '@/utils/analytics';
import { formatDate, formatTime, formatTimeFromHHmm } from '@/utils/date';

import { InternalRoutinesSection } from './components/internal-routines-section';
import { RoutineFilterMenu } from './components/routine-filter-menu';
import {
  RoutineLibraryCompactSection,
  RoutineLibrarySection,
  RoutineTeamTemplatesSection,
  useRoutineTemplateDeepLink,
} from './components/routine-library-section';
/* Planned: restore when routine project assignment is implemented.
import { RoutineProjectDropdownButton, RoutineProjectDropdownSubMenu } from './components/routine-project-menu';
*/
import { useCreateRoutineWithLittlebird } from './hooks/use-create-routine-with-littlebird';
import { useOpenRoutine } from './hooks/use-open-routine';
import { useDeleteRoutine } from './hooks/use-routine-actions';
import {
  filterRoutines,
  splitRoutineFilters,
  toLibraryFrequency,
  useProactiveRoutineChatIds,
} from './routine-filters';
import { CreateRoutineModal } from './routines-create';
import { routinesStore, type RoutineFilter } from './routines.state';

const isSameCalendarDate = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

export const formatRoutineListSchedule = (
  recurrenceRule: string | null,
  formatScheduleTime: (time: string) => string = formatTimeFromHHmm
) => {
  const { frequency, triggerTime, weekDays } =
    parseRoutineRecurrenceRule(recurrenceRule);
  const parsedSchedule = parseRoutineSchedule(recurrenceRule);
  const usesDetailedSummary =
    parsedSchedule.frequency === 'Interval' ||
    parsedSchedule.frequency === 'Hourly' ||
    parsedSchedule.frequency === 'Advanced';

  if (parsedSchedule.isValid && usesDetailedSummary) {
    return formatRoutineScheduleSummary(parsedSchedule, {
      formatTime: formatScheduleTime,
    }).replace(/^Advanced · /, '');
  }

  const displayTime = triggerTime ? formatScheduleTime(triggerTime) : '';
  const weekDayLabel =
    frequency === 'Weekly' && weekDays ? formatRoutineWeekDays(weekDays) : null;
  return [
    frequency,
    weekDayLabel ? `(${weekDayLabel})` : null,
    displayTime ? `at ${displayTime}` : null,
  ]
    .filter(Boolean)
    .join(' ');
};

const formatLastRun = (date: Date) => {
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  if (isSameCalendarDate(date, today)) {
    return `Last run today at ${formatTime(date)}`;
  }

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isSameCalendarDate(date, yesterday)) {
    return `Last run yesterday at ${formatTime(date)}`;
  }

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOfRunDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const daysAgo = Math.round(
    (startOfToday.getTime() - startOfRunDate.getTime()) / 86_400_000
  );

  if (daysAgo > 1 && daysAgo < 7) {
    return `Last run ${formatDate(date, 'EEE')} at ${formatTime(date)}`;
  }

  return `Last run ${formatDate(date, 'MMM d')} at ${formatTime(date)}`;
};

export const AllRoutines = observer(() => {
  const { routines, reports } = useStores();
  const internalRoutines = useVisibleRoutines(routines.userRoutines, {
    includeHidden: true,
  }).filter((routine) => routine.hiddenFromUser);
  const createWithLittlebird = useCreateRoutineWithLittlebird();
  const [isManualSetupOpen, setIsManualSetupOpen] = useState(false);
  const [selectedLibraryItem, setSelectedLibraryItem] =
    useState<RoutineLibraryItem>();
  useRoutineTemplateDeepLink();

  useEffect(() => {
    trackEvent('ROUTINE_LIST_VIEWED');
  }, []);

  const openManualSetup = () => {
    setSelectedLibraryItem(undefined);
    setIsManualSetupOpen(true);
  };
  const openTemplateSetup = (item: RoutineLibraryItem) => {
    setSelectedLibraryItem(item);
    setIsManualSetupOpen(true);
  };
  const setUpManually = () => {
    trackEvent('ROUTINE_NEW_BUTTON_CLICKED');
    openManualSetup();
  };

  const newRoutineMenu = (
    <DropdownMenu.Root>
      <div className="flex overflow-hidden rounded-lg shadow-sm">
        <Page.HeaderButton
          id="new-routine-button"
          icon={Icons.plus}
          className="focus-ring-inset rounded-none pr-3 shadow-none"
          onClick={createWithLittlebird}
        >
          New Routine
        </Page.HeaderButton>
        <DropdownMenu.Trigger asChild>
          <Page.HeaderButton
            size="icon"
            aria-label="New routine options"
            className={cn(
              "focus-ring-inset relative w-8 rounded-none shadow-none before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-[color-mix(in_srgb,var(--accent-foreground)_5%,transparent)] before:content-[''] data-[state=open]:!bg-accent-hover data-[state=open]:!text-accent-foreground data-[dropdown-open=true]:!bg-accent-hover data-[dropdown-open=true]:!text-accent-foreground"
            )}
          >
            <Icons.chevronDown className="size-3.5" />
          </Page.HeaderButton>
        </DropdownMenu.Trigger>
      </div>
      <DropdownMenu.Content
        align="end"
        className="min-w-52"
        fit
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenu.Item onSelect={createWithLittlebird}>
          <Icons.newChat />
          Create with Littlebird
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={setUpManually}>
          <Icons.slidersHorizontal />
          Set up manually
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );

  const createRoutineModal = (
    <CreateRoutineModal
      open={isManualSetupOpen}
      libraryItem={selectedLibraryItem}
      onOpenChange={(open) => {
        setIsManualSetupOpen(open);
        if (!open) setSelectedLibraryItem(undefined);
      }}
    />
  );

  return (
    <ScrollMask className="h-full scrollbar-hide">
      <Page.HeaderActions>
        <RoutinesToolbar compact />
        {newRoutineMenu}
      </Page.HeaderActions>
      <Page.Column top className="flex-1 gap-8 pb-6">
        <RoutinesContent
          onBrowseLibrary={openManualSetup}
          onSelectTemplate={openTemplateSetup}
        />
        <RoutinesLimitWarning />
        <InternalRoutinesSection>
          <RoutinesList
            routines={internalRoutines}
            reports={reports.orderedReports}
          />
        </InternalRoutinesSection>
      </Page.Column>
      {createRoutineModal}
    </ScrollMask>
  );
});

const RoutineLibraryBelowList = observer(function RoutineLibraryBelowList({
  onBrowseLibrary,
  onSelectTemplate,
}: {
  onBrowseLibrary: () => void;
  onSelectTemplate: (item: RoutineLibraryItem) => void;
}) {
  const createWithLittlebird = useCreateRoutineWithLittlebird();
  const { routines } = useStores();
  const { hasReachedLimit } = useRoutinesLimitState();

  if (!routines.userRoutines.length || hasReachedLimit) return null;

  return (
    <RoutineLibraryCompactSection
      onAskLittlebird={createWithLittlebird}
      onBrowseLibrary={onBrowseLibrary}
      onSelectTemplate={onSelectTemplate}
    />
  );
});

const RoutinesToolbar = observer(
  ({ compact = false }: { compact?: boolean }) => {
    const { routines } = useStores();
    const visibleRoutines = useVisibleRoutines(routines.userRoutines);

    if (!routineReadState.hasLoadedRoutines || visibleRoutines.length === 0) {
      return null;
    }

    return (
      <XStack className={compact ? 'items-center gap-3' : 'gap-2'}>
        {compact ? (
          <HeaderSearch
            value={routinesStore.useSearchQuery()}
            onChange={(query) => routinesStore.setSearchQuery(query)}
            placeholder="Search routines..."
            aria-label="Search routines"
          />
        ) : (
          <SearchInput
            value={routinesStore.useSearchQuery()}
            onChange={(query) => routinesStore.setSearchQuery(query)}
            placeholder="Search routines..."
            aria-label="Search routines"
          />
        )}
        <RoutineFilterMenu compact={compact} />
      </XStack>
    );
  }
);

export const showRoutineSuggestions = (
  statusFilters: readonly RoutineFilter[]
): boolean =>
  statusFilters.length === 0 || statusFilters.includes('suggestions');

type RoutinesListProps = {
  routines?: Routine[];
  reports?: Report[];
};

const RoutinesList = observer(({ routines, reports }: RoutinesListProps) => {
  const latestReportByRoutineId = useMemo(() => {
    const map = new Map<number, Report>();
    if (!reports) return map;

    for (const report of reports) {
      const existing = map.get(report.reportTemplateId);
      const isNewer =
        !existing ||
        report.scheduledAt.getTime() > existing.scheduledAt.getTime() ||
        (report.scheduledAt.getTime() === existing.scheduledAt.getTime() &&
          report.referenceId > existing.referenceId);
      if (isNewer) map.set(report.reportTemplateId, report);
    }

    return map;
  }, [reports]);

  return (
    <YStack className="gap-1 no-record">
      {routines?.map((routine) => (
        <RoutineRow
          key={routine.referenceId}
          routine={routine}
          latestReport={
            latestReportByRoutineId.get(routine.referenceId) ?? null
          }
        />
      ))}
    </YStack>
  );
});

type RoutineRowProps = {
  routine: Routine;
  latestReport: Report | null;
};

const RoutineRow = observer(({ routine, latestReport }: RoutineRowProps) => {
  const { openRoutine, openRoutineRun, openRoutineConfiguration } =
    useOpenRoutine();
  const { routines } = useStores();
  const { showModal: showDeleteRoutineModal } = useDeleteRoutine();
  const resume = useResumeRoutine();
  const isResuming = routines.isOperationPending('resume', routine.referenceId);
  const isPausing = routines.isOperationPending('pause', routine.referenceId);
  const [menuOpen, setMenuOpen] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  // const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  void settingsStore.timeFormat;

  const handleClick = (e: React.MouseEvent) => {
    openRoutine(routine.referenceId, e);
  };

  const schedule = formatRoutineListSchedule(routine.recurrenceRule);
  const lastRun = latestReport ? formatLastRun(latestReport.createdAt) : '';
  const hasUnreadRun =
    latestReport !== null &&
    isRoutineRunUnread(latestReport.text, latestReport.lastViewed !== null);
  const configureRoutine = () => {
    openRoutineConfiguration(routine.referenceId);
  };
  const waitingOnYou = routine.waitingOnYou;
  const waitingOnYouButton = waitingOnYou && (
    <Button
      size="xs"
      tooltip={waitingOnYou.reason}
      className="shrink-0 rounded-full"
      onClick={() => openRoutineRun(routine.referenceId, waitingOnYou.reportId)}
    >
      {waitingOnYou.kind === 'reply' ? 'Needs you' : 'Approve'}
    </Button>
  );
  const actions = getRoutineActions(routine);
  const hasActionsMenu = actions.canConfigure || actions.canDelete;
  const iconPicker = (
    <IconPicker
      value={routine.icon ?? DEFAULT_ROUTINE_ICON}
      onChange={(icon) =>
        void routines
          .updateRoutine(routine.referenceId, { icon })
          .catch(() => undefined)
      }
      onOpenChange={setIconPickerOpen}
    >
      <button
        type="button"
        aria-label={`Change icon for ${routine.title}`}
        className="shrink-0 rounded-xl transition-opacity hover:opacity-80 focus-ring"
      >
        <EntityIconChip
          icon={routine.icon ?? DEFAULT_ROUTINE_ICON}
          size={20}
          className="size-12 rounded-xl"
        />
      </button>
    </IconPicker>
  );

  if (routine.isPaused || routine.isAdminPaused) {
    return (
      <XStack
        className={cn(
          'group/paused w-full min-w-0 items-center gap-4 rounded-2xl p-2 transition-colors hover:bg-surface-hover',
          (menuOpen || iconPickerOpen) && 'bg-surface-hover'
        )}
      >
        {iconPicker}
        <button
          type="button"
          onClick={handleClick}
          className="group flex min-w-0 flex-1 text-left"
        >
          <YStack className="min-w-0 flex-1 gap-0.5">
            <XStack className="min-w-0 items-center gap-1.5">
              <span className="truncate text-base font-medium text-foreground-muted">
                {routine.title}
              </span>
              <HiddenFromUserIcon hidden={routine.hiddenFromUser} />
            </XStack>
            <span className="truncate text-xs leading-4 text-foreground-muted">
              {schedule}
            </span>
          </YStack>
        </button>
        {waitingOnYouButton}
        <XStack className="relative min-w-20 shrink-0 items-center justify-end">
          {latestReport && (
            <Text
              aria-label={lastRun}
              className={cn(
                'absolute right-0 top-1/2 w-20 -translate-y-1/2 px-2 text-right text-xs text-foreground-muted pointer-events-none transition-opacity duration-150',
                'group-hover/paused:opacity-0 group-focus-within/paused:opacity-0',
                (menuOpen || routine.isExperimental) && 'opacity-0'
              )}
            >
              <Time date={latestReport.createdAt} />
            </Text>
          )}
          <XStack
            className={cn(
              'mr-2 shrink-0 items-center gap-0.5 opacity-0 pointer-events-none transition-opacity duration-150',
              'touch:opacity-100 touch:pointer-events-auto',
              'group-hover/paused:opacity-100 group-hover/paused:pointer-events-auto',
              'group-focus-within/paused:opacity-100 group-focus-within/paused:pointer-events-auto',
              routine.isExperimental && 'opacity-100 pointer-events-auto',
              menuOpen && 'opacity-100 pointer-events-auto'
            )}
          >
            {routine.isAdminPaused ? (
              <Button
                variant="outline"
                size="sm"
                className="shadow-none"
                disabled
              >
                Paused by admin
              </Button>
            ) : actions.canResume ? (
              <Button
                variant="outline"
                size="sm"
                className="shadow-none"
                disabled={isResuming}
                onClick={() => void resume(routine.referenceId)}
              >
                Resume
              </Button>
            ) : null}
            {hasActionsMenu && (
              <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenu.Trigger asChild>
                  <IconButton
                    icon={Icons.moreHorizontal}
                    size="default"
                    variant="secondary"
                    aria-label={`Actions for ${routine.title}`}
                    className="data-[state=open]:bg-secondary-hover"
                  />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="min-w-40" fit>
                  {/* Planned: restore when routine project assignment is implemented.
                <RoutineProjectDropdownSubMenu />
                */}
                  {actions.canConfigure && (
                    <DropdownMenu.Item onClick={configureRoutine}>
                      <Icons.slidersHorizontal />
                      Configure
                    </DropdownMenu.Item>
                  )}
                  {actions.canResume && (
                    <DropdownMenu.Item
                      onClick={() => void resume(routine.referenceId)}
                    >
                      <Icons.playAlt />
                      Resume
                    </DropdownMenu.Item>
                  )}
                  {actions.canDelete && (
                    <>
                      <DropdownMenu.Separator />
                      <DropdownMenu.Item
                        className="text-danger-9"
                        onClick={() => showDeleteRoutineModal(routine)}
                      >
                        <Icons.trash />
                        Delete
                      </DropdownMenu.Item>
                    </>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            )}
          </XStack>
        </XStack>
      </XStack>
    );
  }

  return (
    <XStack
      className={cn(
        'group/routine-row w-full min-w-0 items-center gap-4 rounded-2xl p-2',
        'transition-colors hover:bg-surface-hover',
        (menuOpen || iconPickerOpen) /* || projectMenuOpen */ &&
          'bg-surface-hover'
      )}
    >
      {iconPicker}
      <button
        type="button"
        onClick={handleClick}
        className="flex min-w-0 flex-1 text-left"
      >
        <YStack className="min-w-0 flex-1 gap-0.5">
          <XStack className="min-w-0 items-center gap-1.5">
            <span className="truncate text-base font-medium text-foreground">
              {routine.title}
            </span>
            {hasUnreadRun && (
              <span
                aria-label="Unread"
                className="size-2 shrink-0 rounded-full bg-green-500"
              />
            )}
            <HiddenFromUserIcon hidden={routine.hiddenFromUser} />
          </XStack>
          <span className="truncate text-xs leading-4 text-foreground-muted">
            {schedule}
          </span>
        </YStack>
      </button>
      {waitingOnYouButton}
      <XStack className="relative min-w-20 shrink-0 items-center justify-end">
        {latestReport && (
          <Text
            aria-label={lastRun}
            className={cn(
              'absolute right-0 top-1/2 w-20 -translate-y-1/2 px-2 text-right text-xs text-foreground-muted pointer-events-none transition-opacity duration-150',
              'group-hover/routine-row:opacity-0 group-focus-within/routine-row:opacity-0',
              (menuOpen || routine.isExperimental) && 'opacity-0'
            )}
          >
            <Time date={latestReport.createdAt} />
          </Text>
        )}
        <XStack
          className={cn(
            'mr-2 shrink-0 items-center gap-0.5 opacity-0 pointer-events-none transition-opacity duration-150',
            'touch:opacity-100 touch:pointer-events-auto',
            'group-hover/routine-row:opacity-100 group-hover/routine-row:pointer-events-auto',
            'group-focus-within/routine-row:opacity-100 group-focus-within/routine-row:pointer-events-auto',
            routine.isExperimental && 'opacity-100 pointer-events-auto',
            menuOpen /* || projectMenuOpen */ &&
              'opacity-100 pointer-events-auto'
          )}
        >
          {/* Planned: restore when routine project assignment is implemented.
        <RoutineProjectDropdownButton onOpenChange={setProjectMenuOpen} />
        */}
          {/* Without a menu to hold it, Pause gets a button of its own */}
          {!hasActionsMenu && actions.canPause && (
            <Button
              variant="outline"
              size="sm"
              className="shadow-none"
              disabled={isPausing}
              onClick={() =>
                void routines
                  .pauseRoutine(routine.referenceId)
                  .catch(() => undefined)
              }
            >
              Pause
            </Button>
          )}
          {hasActionsMenu && (
            <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenu.Trigger asChild>
                <IconButton
                  icon={Icons.moreHorizontal}
                  size="default"
                  variant="secondary"
                  aria-label={`Actions for ${routine.title}`}
                  className="data-[state=open]:bg-secondary-hover"
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end" className="min-w-40" fit>
                {actions.canConfigure && (
                  <DropdownMenu.Item onClick={configureRoutine}>
                    <Icons.slidersHorizontal />
                    Configure
                  </DropdownMenu.Item>
                )}
                {actions.canShare && (
                  <RoutineTeamShareMenuItems routine={routine} />
                )}
                {actions.canPause && (
                  <DropdownMenu.Item
                    onClick={() =>
                      void routines
                        .pauseRoutine(routine.referenceId)
                        .catch(() => undefined)
                    }
                  >
                    <Icons.pause />
                    Pause
                  </DropdownMenu.Item>
                )}
                {actions.canDelete && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item
                      className="text-danger-9"
                      onClick={() => showDeleteRoutineModal(routine)}
                    >
                      <Icons.trash />
                      Delete
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          )}
        </XStack>
      </XStack>
    </XStack>
  );
});

const RoutinesLoadFailed = () => (
  <Empty.Root variant="action">
    <Empty.Media variant="icon">
      <Icons.warnTriangle />
    </Empty.Media>
    <Empty.Header>
      <Empty.Title>Couldn&apos;t load routines</Empty.Title>
      <Empty.Description>
        Check your connection and try again.
      </Empty.Description>
    </Empty.Header>
    <Empty.Actions>
      <Button variant="outline" onClick={refreshRoutineReads}>
        Retry
      </Button>
    </Empty.Actions>
  </Empty.Root>
);

const RoutinesListLoading = () => {
  return (
    <YStack className="gap-1">
      {[...Array(6)].map((_, index) => (
        <Skeleton key={index} className="h-16 rounded-2xl" />
      ))}
    </YStack>
  );
};

const RoutinesContent = observer(function RoutinesContent({
  onBrowseLibrary,
  onSelectTemplate,
}: {
  onBrowseLibrary: () => void;
  onSelectTemplate: (item: RoutineLibraryItem) => void;
}) {
  const createWithLittlebird = useCreateRoutineWithLittlebird();
  const { routines: routineStore, reports: reportStore } = useStores();
  useRoutineRefresh();
  const hasFailed = routineReadState.hasFailedRoutines;
  const isLoading = !routineReadState.hasLoadedRoutines && !hasFailed;
  const routines = useVisibleRoutines(routineStore.userRoutines);
  const reports = reportStore.orderedReports;
  const routineFilters = routinesStore.useRoutineFilters();
  const searchQuery = routinesStore.useSearchQuery();
  const trimmedQuery = searchQuery.trim();
  const {
    statusFilters: selectedStatusFilters,
    scheduleFilters: selectedScheduleFilters,
  } = splitRoutineFilters(routineFilters);
  const showSuggestions = showRoutineSuggestions(selectedStatusFilters);

  const proactiveChatIds = useProactiveRoutineChatIds();
  const filteredRoutines = useMemo(
    () =>
      filterRoutines(routines, routineFilters, trimmedQuery, proactiveChatIds),
    [routines, routineFilters, trimmedQuery, proactiveChatIds]
  );

  const isFilteredRoutinesEmpty = filteredRoutines.length === 0;
  const hasAnyRoutines = routines.length > 0;
  const isSearchActive = trimmedQuery !== '';
  const isFilterActive = isSearchActive || routineFilters.length > 0;
  const activeRoutines = filteredRoutines.filter(
    (routine) => !routine.isPaused && !routine.isExperimental
  );
  const experimentalRoutines = filteredRoutines.filter(
    (routine) => routine.isExperimental
  );
  const pausedRoutines = filteredRoutines.filter(
    (routine) => routine.isPaused && !routine.isExperimental
  );
  const selectedLibraryFrequencies =
    selectedScheduleFilters.map(toLibraryFrequency);

  if (isLoading) return <RoutinesListLoading />;

  // A failed read is not an empty account: showing the first-run empty state
  // here would invite the user to recreate routines they already have.
  if (hasFailed && !hasAnyRoutines) return <RoutinesLoadFailed />;

  if (
    isFilteredRoutinesEmpty &&
    isFilterActive &&
    hasAnyRoutines &&
    !showSuggestions
  ) {
    return (
      <Empty.Root>
        <Empty.Media variant="icon">
          <Icons.search />
        </Empty.Media>
        <Empty.Header>
          <Empty.Title>No routines found</Empty.Title>
          <Empty.Description>
            Try adjusting your search or filters.
          </Empty.Description>
        </Empty.Header>
      </Empty.Root>
    );
  }

  const emptyState = (
    <YStack className="gap-8">
      <RoutinesEmpty hasAnyRoutines={hasAnyRoutines} />
      {!isFilterActive && (
        <RoutineTeamTemplatesSection onSelectTemplate={onSelectTemplate} />
      )}
    </YStack>
  );

  if (isFilteredRoutinesEmpty && !showSuggestions) {
    return emptyState;
  }

  if (
    isFilteredRoutinesEmpty &&
    routineFilters.length === 0 &&
    !isSearchActive
  ) {
    return emptyState;
  }

  return (
    <YStack className="gap-8">
      {activeRoutines.length ? (
        <RoutinesList routines={activeRoutines} reports={reports} />
      ) : null}
      <RoutineTeamTemplatesSection onSelectTemplate={onSelectTemplate} />
      {showSuggestions &&
        (routineFilters.length > 0 || isSearchActive ? (
          <RoutineLibraryCompactSection
            frequencies={selectedLibraryFrequencies}
            query={trimmedQuery}
            onAskLittlebird={createWithLittlebird}
            onBrowseLibrary={onBrowseLibrary}
            onSelectTemplate={onSelectTemplate}
          />
        ) : (
          <RoutineLibraryBelowList
            onBrowseLibrary={onBrowseLibrary}
            onSelectTemplate={onSelectTemplate}
          />
        ))}
      {experimentalRoutines.length ? (
        <YStack className="gap-2">
          <YStack className="gap-0.5 px-2">
            <Text className="text-sm text-foreground-muted">
              Proactive routines
            </Text>
            <Text className="text-xs text-foreground-muted">
              Timely updates from Littlebird. You can pause or resume your copy.
            </Text>
          </YStack>
          <RoutinesList routines={experimentalRoutines} reports={reports} />
        </YStack>
      ) : null}
      {pausedRoutines.length ? (
        <YStack className="gap-2">
          <Text className="px-2 text-sm text-foreground-muted">Paused</Text>
          <RoutinesList routines={pausedRoutines} reports={reports} />
        </YStack>
      ) : null}
    </YStack>
  );
});

type RoutinesEmptyProps = {
  hasAnyRoutines: boolean;
};

const ROUTINE_STEP_ICONS: {
  Icon: LBIcon;
  restAngle: number;
  jumpAngle: number;
  restY: number;
  large: boolean;
}[] = [
  { Icon: Icons.clock, restAngle: -4, jumpAngle: -7, restY: 0, large: false },
  {
    Icon: Icons.sparkles,
    restAngle: 0,
    jumpAngle: -5,
    restY: -6,
    large: true,
  },
  { Icon: Icons.notepad, restAngle: 4, jumpAngle: 7, restY: 0, large: false },
];

const JumpyIcon = ({
  Icon,
  delay,
  restAngle,
  jumpAngle,
  restY,
  large,
}: {
  Icon: LBIcon;
  delay: number;
  restAngle: number;
  jumpAngle: number;
  restY: number;
  large: boolean;
}) => {
  const controls = useAnimationControls();

  useEffect(() => {
    controls.start({
      opacity: 1,
      y: restY,
      rotate: restAngle,
      transition: { duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] },
    });
  }, [controls, delay, restAngle, restY]);

  const jump = () => {
    controls.start({
      y: [restY, restY - 12, restY],
      rotate: [restAngle, restAngle + jumpAngle, restAngle],
      transition: { duration: 0.4, ease: 'easeOut', times: [0, 0.4, 1] },
    });
  };

  return (
    <motion.button
      type="button"
      onClick={jump}
      initial={{ opacity: 0, y: restY + 8, rotate: restAngle }}
      animate={controls}
      className={cn(
        'rounded-lg border border-border-strongest',
        'bg-secondary text-foreground-muted',
        'flex items-center justify-center',
        large ? 'size-10' : 'size-9'
      )}
    >
      <Icon className={large ? 'size-5' : 'size-4'} />
    </motion.button>
  );
};

const RoutinesEmptyAnimation = () => (
  <XStack className="items-center gap-2">
    {ROUTINE_STEP_ICONS.map((cfg, i) => (
      <JumpyIcon key={i} delay={0.1 + i * 0.12} {...cfg} />
    ))}
  </XStack>
);

const openCreateRoutine = () => {
  trackEvent('ROUTINE_NEW_BUTTON_CLICKED');
  ModalsManager.show(CreateRoutineModal, {});
};

const RoutinesFirstRunEmpty = () => (
  <Empty.Root>
    <Empty.Media>
      <RoutinesEmptyAnimation />
    </Empty.Media>
    <Empty.Header>
      <Empty.Title>Set your AI on a schedule</Empty.Title>
      <Empty.Description>
        Describe what you want to gather, pick a schedule, and Littlebird
        delivers a note when it’s ready.
      </Empty.Description>
    </Empty.Header>
  </Empty.Root>
);

const RoutinesEmpty = ({ hasAnyRoutines }: RoutinesEmptyProps) => {
  if (!hasAnyRoutines) {
    return (
      <YStack className="gap-10">
        <RoutinesFirstRunEmpty />
        <RoutineLibrarySection />
      </YStack>
    );
  }

  return (
    <Empty.Root variant="action">
      <Empty.Header>
        <Empty.Title>Create your next routine</Empty.Title>
        <Empty.Description>
          Set up automated reports that generate at your chosen time and
          frequency.
        </Empty.Description>
      </Empty.Header>
      <Empty.Actions>
        <Button onClick={openCreateRoutine}>Create routine</Button>
      </Empty.Actions>
    </Empty.Root>
  );
};
